import { BlogPost as BlogPostType } from '@/types/blog';
import { formatDate, calculateReadingTime } from '@/utils/blogUtils';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, Clock, User, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import OptimizedImage from '@/components/ui/OptimizedImage';
import TableOfContents from '@/components/blog/TableOfContents';
import InBodyToc from '@/components/blog/InBodyToc';

// X (Twitter) Icon Component
const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// LinkedIn Icon Component
const LinkedInIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

interface BlogPostProps {
  post: BlogPostType;
}

const BlogPost = ({ post }: BlogPostProps) => {
  // Use pre-calculated reading time if available, otherwise calculate it
  const readingTime = post.readingTime || calculateReadingTime(post.content);

  return (
    <article className="max-w-4xl mx-auto px-4 py-8">
      {/* Back to Blog Button */}
      <div className="mb-8">
        <Button variant="ghost" asChild className="text-coderide-light-blue hover:text-coderide-purple hover:bg-coderide-light-blue/10">
          <Link to="/blog" className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Blog
          </Link>
        </Button>
      </div>

      {/* Article Header */}
      <header className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 leading-tight">
          {post.title}
        </h1>
        
        <p className="text-xl text-muted-foreground mb-6 leading-relaxed">
          {post.description}
        </p>
        
        {/* Meta Information */}
        <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground border-b border-border pb-6">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span>{post.author}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>{formatDate(post.date)}</span>
          </div>
          
          {post.modifiedTime && (
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              <span>Updated {formatDate(post.modifiedTime)}</span>
            </div>
          )}
          
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>{readingTime}</span>
          </div>
        </div>
      </header>

      {/* Hero Image */}
      {post.image && (
        <div className="mb-8">
          <OptimizedImage
            src={post.image}
            alt={post.imageAlt || post.title}
            width={1200}
            height={600}
            priority={true}
            className="w-full h-64 md:h-96 object-cover rounded-lg shadow-lg"
          />
        </div>
      )}

      {/* Main Content Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
        {/* Article Content */}
        <div className="xl:col-span-3">
          {/* In-body ToC for mobile/tablet. Sidebar ToC remains on xl+. */}
          <InBodyToc content={post.content} className="block xl:hidden mb-6" title="Quick navigation" />
          <div 
            className="prose prose-lg prose-invert max-w-none
              prose-headings:text-white prose-headings:font-semibold
              prose-h1:text-3xl prose-h1:mb-6 prose-h1:mt-8
              prose-h2:text-2xl prose-h2:mb-6 prose-h2:mt-10 prose-h2:text-coderide-light-blue prose-h2:border-b prose-h2:border-gray-700 prose-h2:pb-3
              prose-h3:text-xl prose-h3:mb-4 prose-h3:mt-8 prose-h3:text-coderide-purple
              prose-p:text-gray-200 prose-p:leading-relaxed prose-p:mb-6 prose-p:text-lg
              prose-strong:text-white prose-strong:font-semibold
              prose-ul:text-gray-200 prose-ul:mb-6 prose-ul:pl-6
              prose-li:text-gray-200 prose-li:mb-2 prose-li:leading-relaxed
              prose-ol:text-gray-200 prose-ol:mb-6 prose-ol:pl-6
              prose-a:text-coderide-light-blue prose-a:no-underline hover:prose-a:underline
              prose-blockquote:border-l-4 prose-blockquote:border-coderide-light-blue prose-blockquote:text-gray-200 prose-blockquote:bg-gray-800/50 prose-blockquote:p-4 prose-blockquote:rounded-r-lg prose-blockquote:my-6
              prose-code:text-coderide-purple prose-code:bg-gray-800 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm
              prose-pre:bg-gray-900 prose-pre:border prose-pre:border-gray-700 prose-pre:rounded-lg prose-pre:p-4 prose-pre:my-6
            "
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </div>

        {/* Table of Contents Sidebar */}
        <div className="xl:col-span-1">
          <div className="xl:sticky xl:top-8">
            <TableOfContents 
              content={post.content}
              readingTime={readingTime}
              className="hidden xl:block"
            />
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="mt-12 overflow-hidden bg-gradient-to-r from-coderide-light-blue/10 to-coderide-purple/10 rounded-lg border border-coderide-light-blue/20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          {/* Content Section */}
          <div className="p-6">
            <h3 className="text-xl font-semibold text-foreground mb-3">
              Ready to transform your AI development workflow?
            </h3>
            <p className="text-muted-foreground mb-4">
              Experience seamless AI development with CodeRide. Eliminate context reset and build better software faster.
            </p>
            <Button 
              variant="primary"
              showArrow={true}
              onClick={() => window.open('https://app.coderide.ai/auth/sign-up', '_blank')}
            >
              Get Started Now
            </Button>
          </div>
          
          {/* Image Section */}
          <div className="relative h-48 md:h-full min-h-[200px]">
            <OptimizedImage
              src="https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/Coderide-og-X.jpg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9jOTljZjI2OC04OTEzLTRhYjktYmI4YS0yMTE1MjQ2YzRjNjQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL0NvZGVyaWRlLW9nLVguanBnIiwiaWF0IjoxNzUzODA0NzIzLCJleHAiOjQ4NzU4Njg3MjN9.nGnNc_a0vXNwGfT9OhrigDMYfncDzsHghY69ByVyyxk"
              alt="CodeRide - AI-powered development platform"
              width={600}
              height={300}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            {/* Overlay for better text readability if needed */}
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/20 md:to-transparent"></div>
          </div>
        </div>
      </div>

      {/* Share Section */}
      <div className="mt-12 pt-8 border-t border-gray-700">
        <div className="text-center">
          <p className="text-sm text-gray-400 mb-3">
            Share this article
          </p>
          <div className="flex gap-3 justify-center">
            <Button 
              variant="outline" 
              size="default"
              className="border-coderide-light-blue text-coderide-light-blue hover:bg-coderide-light-blue hover:text-white transition-colors p-3"
              onClick={() => {
                // Only run in browser environment
                if (typeof window === 'undefined') return;
                const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`;
                window.open(url, '_blank');
              }}
              title="Share on X"
            >
              <XIcon className="h-5 w-5" />
            </Button>
            <Button 
              variant="outline" 
              size="default"
              className="border-coderide-light-blue text-coderide-light-blue hover:bg-coderide-light-blue hover:text-white transition-colors p-3"
              onClick={() => {
                // Only run in browser environment
                if (typeof window === 'undefined') return;
                const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`;
                window.open(url, '_blank');
              }}
              title="Share on LinkedIn"
            >
              <LinkedInIcon className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
};

export default BlogPost;
